// src/App.js
import React from "react";
import { Routes, Route } from "react-router-dom";
import { useSessionTimeout } from "./hooks/useSessionTimeout";
import { useAuthInitializer } from "./hooks/useAuthInitializer";

// Route guard components
import ProtectedRoute from "./components/ProtectedRoute";
import UnprotectedRoute from "./components/UnprotectedRoute";

// Page imports
import TwoTabsPage from "./pages/TwoTabsPage";
import Dashboard from "./pages/Dashboard";
import CompaniesDashboard from "./pages/CompaniesDashboard";
import ResumeScreeingCandidates from "./pages/ResumeScreening/ResumeScreeingCandidates";
import ResumeScreeningCandidateOverallResult from "./pages/ResumeScreening/ResumeScreeningCandidateOverallResult";
import ResumeScreeningCandidateFullReport from "./pages/ResumeScreening/ResumeScreeningCandidateFullReport";
import MyAssessments from "./pages/Assessments/Assessments";
import AssessmentsCreateOrEdit from "./pages/Assessments/AssessmentsCreateOrEdit";
import InterviewResultsCandidates from "./pages/InterviewResults/InterviewResultsCandidates";
import InterviewResultsCandidateOverallResult from "./pages/InterviewResults/InterviewResultsCandidateOverallResult";
import InterviewResultsCandiateFullReport from "./pages/InterviewResults/InterviewResultsCandiateFullReport";
import GetgeekLogin from "./components/GetgeekLogin";
import OTPVerification from "./components/OTPVerification";
import Recruiters from "./pages/Recruiters";
import ContactData from "./pages/ContactData";
import CustomerSupport from "./pages/CustomerSupport";
import GetgeekFeedback from "./pages/GetgeekFeedback";
import Result from "./pages/Result";
import OffersManagement from "./pages/OffersManagement";
import RoiLeads from "./pages/RoiLeads";
import OfferLeads from "./pages/OfferLeads";
import UserGuides from "./pages/UserGuides";
import UserGuideForm from "./pages/UserGuideForm";
import NewsLetter from "./pages/NewsLetter";
import ServerStatus from "./pages/ServerStatus";
import Leads from "./pages/Leads";
import ConnectSocials from "./pages/ConnectSocials";
import LinkedInCallbackPage from "./pages/LinkedInCallbackPage";

function App() {
  useSessionTimeout(); // Session timeout hook
  useAuthInitializer(); // Initialize authentication state

  return (
    <Routes>
      {/* Unprotected routes - only accessible when not authenticated */}
      <Route
        path="/"
        element={
          <UnprotectedRoute>
            <GetgeekLogin />
          </UnprotectedRoute>
        }
      />

      <Route
        path="OTPVerification"
        element={
          <UnprotectedRoute>
            <OTPVerification />
          </UnprotectedRoute>
        }
      />

      {/* LinkedIn OAuth popup callback (must stay public for SDK) */}
      <Route path="/linkedin" element={<LinkedInCallbackPage />} />

      {/* Protected routes - require authentication */}
      <Route
        path="/ConnectSocials"
        element={
          <ProtectedRoute>
            <ConnectSocials />
          </ProtectedRoute>
        }
      />
      <Route
        path="/Dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ServerStatus"
        element={
          <ProtectedRoute>
            <ServerStatus />
          </ProtectedRoute>
        }
      />
       <Route
        path="/GetgeekLeads"
        element={
          <ProtectedRoute>
            <Leads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/CompaniesDashboard"
        element={
          <ProtectedRoute>
            <CompaniesDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/TwoTabsPage"
        element={
          <ProtectedRoute>
            <TwoTabsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/Recruiters"
        element={
          <ProtectedRoute>
            <Recruiters />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ContactData"
        element={
          <ProtectedRoute>
            <ContactData />
          </ProtectedRoute>
        }
      />
      <Route
        path="/CustomerSupport"
        element={
          <ProtectedRoute>
            <CustomerSupport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/GetgeekFeedback"
        element={
          <ProtectedRoute>
            <GetgeekFeedback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/Result"
        element={
          <ProtectedRoute>
            <Result />
          </ProtectedRoute>
        }
      />
      <Route
        path="/OffersManagement"
        element={
          <ProtectedRoute>
            <OffersManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/RoiLeads"
        element={
          <ProtectedRoute>
            <RoiLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/OfferLeads"
        element={
          <ProtectedRoute>
            <OfferLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/NewsLetter"
        element={
          <ProtectedRoute>
            <NewsLetter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/UserGuides"
        element={
          <ProtectedRoute>
            <UserGuides />
          </ProtectedRoute>
        }
      />
      <Route
        path="/UserGuides/create"
        element={
          <ProtectedRoute>
            <UserGuideForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/UserGuides/edit/:id"
        element={
          <ProtectedRoute>
            <UserGuideForm />
          </ProtectedRoute>
        }
      />
      {/* Protected routes with parameters */}
      <Route
        path="resume-screening-candidates/:postId"
        element={
          <ProtectedRoute>
            <ResumeScreeingCandidates />
          </ProtectedRoute>
        }
      />
      <Route
        path="resume-screening-candidates/:postId/candidate-result/:resumeId"
        element={
          <ProtectedRoute>
            <ResumeScreeningCandidateOverallResult />
          </ProtectedRoute>
        }
      />
      <Route
        path="resume-screening-candidates/:postId/candidate-result/:resumeId/full-report"
        element={
          <ProtectedRoute>
            <ResumeScreeningCandidateFullReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="create"
        element={
          <ProtectedRoute>
            <AssessmentsCreateOrEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="my-assessments"
        element={
          <ProtectedRoute>
            <MyAssessments />
          </ProtectedRoute>
        }
      />
      <Route
        path="interview-candidates/:id/:id_type"
        element={
          <ProtectedRoute>
            <InterviewResultsCandidates />
          </ProtectedRoute>
        }
      />
      <Route
        path="interview-candidates/:postId/candidate-result/:resumeId"
        element={
          <ProtectedRoute>
            <InterviewResultsCandidateOverallResult />
          </ProtectedRoute>
        }
      />
      <Route
        path="interview-candidates/:postId/candidate-result/:resumeId/full-report"
        element={
          <ProtectedRoute>
            <InterviewResultsCandiateFullReport />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
